import { FC, KeyboardEvent, MouseEvent, ReactElement } from 'react';
import { Card, CardFooter } from 'react-bootstrap';
import i18next from 'i18next';
import { ExerciseDto } from '../../../../../../shared/dtos/exercise.dto';
import MediaPlayer from '../../../../components/MediaPlayer';

type ExercisePickerCardProps = {
  exercise: ExerciseDto;
  isSelected: boolean;
  isPlaying: boolean;
  onToggleSelection: (exercise: ExerciseDto) => void;
  onRequestPlay: (exercise: ExerciseDto) => void;
};

const ExercisePickerCard: FC<ExercisePickerCardProps> = ({
  exercise,
  isSelected,
  isPlaying,
  onToggleSelection,
  onRequestPlay,
}: ExercisePickerCardProps): ReactElement => {
  const name: string = i18next.language === 'en' ? exercise.enName : exercise.deName;

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>): void => {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    onToggleSelection(exercise);
  };

  return (
    <Card
      role="button"
      tabIndex={0}
      aria-pressed={isSelected}
      aria-label={name}
      onClick={() => onToggleSelection(exercise)}
      onKeyDown={onKeyDown}
      className={`h-100 ${isSelected ? 'primary-border-2' : 'border-2'}`}
      style={{ cursor: 'pointer' }}
    >
      <div
        className="position-relative"
        onClick={(event: MouseEvent<HTMLDivElement>) => isPlaying && event.stopPropagation()}
      >
        <MediaPlayer
          exercise={exercise}
          cornerRadius={0}
          lazy
          isPlaying={isPlaying}
          onRequestPlay={() => onRequestPlay(exercise)}
        />

        {!isPlaying && (
          <span
            aria-hidden
            className={
              'position-absolute top-0 end-0 m-2 d-flex align-items-center justify-content-center rounded-circle ' +
              (isSelected ? 'bg-primary text-white' : 'bg-white text-muted border')
            }
            style={{ width: '2.4rem', height: '2.4rem' }}
          >
            <i className={`fas ${isSelected ? 'fa-check' : 'fa-plus'}`} />
          </span>
        )}
      </div>

      <CardFooter className="bg-transparent">
        <span
          className="font-15 fw-bold"
          title={name}
          style={{
            display: '-webkit-box',
            WebkitLineClamp: 2,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            height: '4.4rem',
            lineHeight: '2.2rem',
          }}
        >
          {name}
        </span>
      </CardFooter>
    </Card>
  );
};

export default ExercisePickerCard;
